import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ForecastCard from "@/components/feature/ForecastCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories, forecasts } from "@/mocks/forecast";

export default function Topics() {
  usePageMeta({
    title: "瓜田分类 - 看料大瓜 | 按领域蹲守预告前瞻",
    description:
      "看料大瓜瓜田分类页，按影视圈、音乐圈、综艺坊、网红直播、商业瓜与文体圈划分前瞻方向，帮你找到最值得蹲守的瓜田预警与爆料线索。",
    keywords: "看料大瓜分类,瓜田预警,影视圈前瞻,商业瓜新闻,蹲守爆料",
    path: "/topics",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "瓜田分类",
        description: "看料大瓜按领域划分的预告前瞻分类。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "瓜田分类", item: "/topics" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "瓜田分类列表",
        itemListElement: categories.map((cat, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: cat.name,
          description: cat.desc,
          url: `/forecast?category=${cat.id}`,
        })),
      },
    ],
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "瓜田分类" }]} />
          <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-5xl">
            瓜田分类地图
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            不同领域的信号长得不一样。影视圈看物料与排期，商业瓜看人事与资本动作。选择一个方向，我们只推送那个圈子的预警。
          </p>
          <div className="mt-7 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className={cat.icon} />
                {cat.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {categories.map((cat, index) => {
        const list = forecasts.filter((item) => item.categoryId === cat.id).slice(0, 2);
        const alt = index % 2 === 1;
        return (
          <section
            key={cat.id}
            id={cat.id}
            className={`scroll-mt-24 ${alt ? "bg-background-100" : "bg-background-50"}`}
          >
            <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
                <div className="lg:col-span-4">
                  <Reveal>
                    <span className="flex h-14 w-14 items-center justify-center rounded-md bg-primary-500 text-2xl text-background-50">
                      <i className={cat.icon} />
                    </span>
                    <h2 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                      {cat.name}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{cat.desc}</p>
                    <p className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
                      <i className="ri-file-list-3-line" />
                      累计 {cat.count} 条前瞻
                    </p>
                    <Link
                      to={`/forecast?category=${cat.id}`}
                      className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-950 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
                    >
                      进入 {cat.name}
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </Reveal>
                </div>

                <div className="lg:col-span-8">
                  {list.length > 0 ? (
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                      {list.map((item, itemIndex) => (
                        <Reveal key={item.id} delay={itemIndex * 80}>
                          <ForecastCard item={item} />
                        </Reveal>
                      ))}
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[200px] flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50/60 px-6 py-10 text-center">
                      <i className="ri-inbox-archive-line text-3xl text-foreground-400" />
                      <p className="mt-3 text-sm text-foreground-500">
                        该分类暂无公开发布的前瞻，新的信号整理中。
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}